import {
  BarChart3,
  BriefcaseBusiness,
  Building2,
  Factory,
  FileSignature,
  GraduationCap,
  Home,
  Truck,
  Users,
  Wrench,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { hrItems } from '../pages/HR';
import { logisticsItems } from '../pages/Logistics';
import { managementItems } from '../pages/Management';
import { montazItems } from '../pages/Montaz';
import { organizationItems } from '../pages/Organization';
import { productionItems } from '../pages/Production';
import { salesItems } from '../pages/Sales';
import { notificationService } from '../services/api';
import FeedbackWidget from './FeedbackWidget';

const MenuIcon = ({ name }) => {
  const paths = {
    user: <><path d="M20 21a8 8 0 0 0-16 0" /><circle cx="12" cy="7" r="4" /></>,
    settings: <><path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z" /><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.83 2.83-.06-.06a1.7 1.7 0 0 0-1.88-.34 1.7 1.7 0 0 0-1.03 1.56V21h-4v-.08A1.7 1.7 0 0 0 8.95 19.4a1.7 1.7 0 0 0-1.88.34l-.06.06-2.83-2.83.06-.06A1.7 1.7 0 0 0 4.58 15 1.7 1.7 0 0 0 3 14H3v-4h.08A1.7 1.7 0 0 0 4.6 8.95a1.7 1.7 0 0 0-.34-1.88l-.06-.06 2.83-2.83.06.06A1.7 1.7 0 0 0 9 4.58 1.7 1.7 0 0 0 10 3h4a1.7 1.7 0 0 0 1.03 1.58 1.7 1.7 0 0 0 1.88-.34l.06-.06 2.83 2.83-.06.06a1.7 1.7 0 0 0-.34 1.88A1.7 1.7 0 0 0 21 10v4a1.7 1.7 0 0 0-1.6 1Z" /></>,
    bell: <><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></>,
    users: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></>,
    shield: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" /><path d="m9 12 2 2 4-4" /></>,
    message: <><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z" /><path d="M8 9h8M8 13h5" /></>,
    fileText: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><path d="M14 2v6h6M8 13h8M8 17h6" /></>,
    megaphone: <><path d="m3 11 18-5v12L3 14v-3Z" /><path d="M11.6 16 13 21H7l-1.5-6" /></>,
    logout: <><path d="M10 17l5-5-5-5M15 12H3" /><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /></>,
  };

  return (
    <svg className="profile-menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[name]}
    </svg>
  );
};

const NavigationIcon = ({ icon }) => {
  if (typeof icon === 'string') return icon;
  const Icon = icon;
  return <Icon size={20} strokeWidth={1.8} aria-hidden="true" />;
};

const Layout = ({ children }) => {
  const { 
    user, 
    logout, 
    isAdmin,
    hasRole,
    hasPermission,
    canAccessSales,
    canAccessLogistics,
    canAccessLogisticsStojaki,
    canAccessHR,
    canAccessTrainings,
    canAccessProduction,
    canAccessProductionMonitor,
    canAccessProductionSchucoMonitor,
    canAccessProductionBlinds,
    canAccessProductionGlassMonitor,
    canAccessProductionDocuments,
    canAccessProductionOther,
    canAccessManagement,
    canAccessGoogleReviews,
    canAccessOrderAnalytics,
    canAccessToolAnalytics,
    canAccessOrganization,
    canAccessMontaz,
  } = useAuth();
  const isClient = hasRole('client');
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedMobileGroups, setExpandedMobileGroups] = useState({});
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const profileMenuRef = useRef(null);
  const mobileMenuTriggerRef = useRef(null);
  const mobileMenuCloseRef = useRef(null);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const data = await notificationService.getNotifications(20, false);
      setUnreadCount(data.unread_count);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, []);

  // Fetch notifications on mount and poll every 60 seconds
  useEffect(() => {
    if (isClient) return undefined;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications, isClient]);

  useEffect(() => {
    setIsProfileMenuOpen(false);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    mobileMenuCloseRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    setAvatarLoadFailed(false);
  }, [user?.avatar]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!profileMenuRef.current?.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsProfileMenuOpen(false);
        setIsMobileMenuOpen(false);
        mobileMenuTriggerRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const getAvatarUrl = () => {
    if (user?.avatar) {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      return `${baseUrl}/auth/avatars/${user.avatar}`;
    }
    return null;
  };

  const getUserInitials = () => {
    const nameParts = user?.full_name?.trim().split(/\s+/).filter(Boolean) || [];
    if (nameParts.length === 0) return 'U';
    if (nameParts.length === 1) return nameParts[0][0].toLocaleUpperCase('pl-PL');
    return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toLocaleUpperCase('pl-PL');
  };

  const avatarUrl = getAvatarUrl();
  const showAvatarImage = avatarUrl && !avatarLoadFailed;

  const profileMenuItems = isClient ? [] : [
    { path: '/profile', label: 'Mój profil', icon: 'user' },
    { path: '/settings', label: 'Ustawienia', icon: 'settings' },
    { path: '/notifications', label: 'Powiadomienia', icon: 'bell', badge: unreadCount },
    { path: '/colleagues', label: 'Moi współpracownicy', icon: 'users' },
  ];

  // Check if user can manage content (news or any type of announcements)
  const canManageContent = () => 
    hasPermission('content:manage_news') || 
    hasPermission('content:manage_announcements') ||
    hasPermission('content:manage_announcements_global') ||
    hasPermission('content:manage_announcements_department');

  const adminContentMenuItems = [
    isAdmin() && { path: '/admin', label: 'Panel administracyjny', icon: 'shield' },
    hasPermission('feedback:manage') && { path: '/feedback-management', label: 'Feedback', icon: 'message' },
    canManageContent() && { path: '/content-management', label: 'Zarządzanie ogłoszeniami', icon: 'fileText' },
    hasPermission('content:manage_announcements') && { path: '/admin-announcements', label: 'Wszystkie ogłoszenia', icon: 'megaphone' },
  ].filter(Boolean);

  // Filter logistics items based on permissions
  const filteredLogisticsItems = logisticsItems.filter(item => {
    if (item.path === '/logistics/stojaki-auto-email') return canAccessLogisticsStojaki();
    return true;
  });

  // Filter production items based on permissions
  const filteredProductionItems = productionItems.filter(item => {
    if (item.path === '/production/order-monitoring') return canAccessProductionMonitor();
    if (item.path === '/production/schuco-monitoring') return canAccessProductionSchucoMonitor();
    if (item.path === '/production/blinds') return canAccessProductionBlinds();
    if (item.path === '/production/glass-monitoring') return canAccessProductionGlassMonitor();
    if (item.path === '/production/documents') return canAccessProductionDocuments();
    if (item.path === '/production/other') return canAccessProductionOther();
    return true;
  });

  // Filter management items based on permissions
  const filteredManagementItems = managementItems.filter(item => {
    if (item.path === '/management/order-analytics') return canAccessOrderAnalytics();
    if (item.path === '/management/google-reviews') return canAccessGoogleReviews();
    if (item.path === '/management/tool-analytics') return canAccessToolAnalytics();
    return false;
  });

  // Filter sales items based on permissions
  const canAccessSalesItem = item => {
    if (item.requiresPermission) {
      if (item.requiresPermission.includes('|')) {
        const permissions = item.requiresPermission.split('|');
        return permissions.some(perm => hasPermission(perm.trim()));
      }
      return hasPermission(item.requiresPermission);
    }
    return true;
  };

  const filteredSalesItems = salesItems
    .filter(canAccessSalesItem)
    .map(item => ({
      ...item,
      children: item.children?.filter(canAccessSalesItem),
    }));

  // Filter organization items based on permissions
  const filteredOrganizationItems = organizationItems.filter(item => {
    if (item.requiresPermission) {
      if (item.requiresPermission.includes('|')) {
        const permissions = item.requiresPermission.split('|');
        return permissions.some(perm => hasPermission(perm.trim()));
      }
      return hasPermission(item.requiresPermission);
    }
    return true;
  });

  // Filter Montaż items based on permissions
  const filteredMontazItems = montazItems.filter(item => {
    if (item.requiresPermission) {
      return hasPermission(item.requiresPermission) || isAdmin();
    }
    return true;
  });

  // Filter HR items based on permissions (admins have full access)
  const filteredHrItems = hrItems.filter(item => {
    if (item.requiresPermission) return hasPermission(item.requiresPermission) || isAdmin();
    return true;
  });

  // Build nav items based on permissions
  const allNavItems = [
      {
      path: '/clients',
      label: 'Klienci',
      icon: Users,
      hasDropdown: true,
      dropdownItems: [{ path: '/clients/aluminium-offers', label: 'Oferty aluminium', icon: FileSignature }],
      canAccess: () => hasPermission('clients:aluminium_offers'),
    },
    { path: '/home', label: 'Strona główna', icon: Home, canAccess: () => true },
    { 
      path: '/hr', 
      label: 'HR', 
      icon: Users,
      hasDropdown: filteredHrItems.length > 0,
      dropdownItems: filteredHrItems,
      canAccess: canAccessHR 
    },
    { path: '/trainings', label: 'Szkolenia', icon: GraduationCap, canAccess: canAccessTrainings },
    { 
      path: '/sales', 
      label: 'Dział Handlowy', 
      icon: BriefcaseBusiness,
      hasDropdown: true,
      dropdownItems: filteredSalesItems,
      canAccess: canAccessSales 
    },
    { 
      path: '/organization', 
      label: 'Organizacja', 
      icon: Building2,
      hasDropdown: true,
      dropdownItems: filteredOrganizationItems,
      canAccess: canAccessOrganization 
    },
    { 
      path: '/logistics', 
      label: 'Logistyka', 
      icon: Truck,
      hasDropdown: true,
      dropdownItems: filteredLogisticsItems,
      canAccess: canAccessLogistics 
    },
    { 
      path: '/production', 
      label: 'Produkcja', 
      icon: Factory,
      hasDropdown: true, 
      dropdownItems: filteredProductionItems,
      canAccess: canAccessProduction 
    },
    { 
      path: '/montaz', 
      label: 'Montaż', 
      icon: Wrench,
      hasDropdown: filteredMontazItems.length > 0,
      dropdownItems: filteredMontazItems,
      canAccess: canAccessMontaz 
    },
    { 
      path: '/management', 
      label: 'Zarząd', 
      icon: BarChart3,
      hasDropdown: true, 
      dropdownItems: filteredManagementItems,
      canAccess: canAccessManagement 
    },
  ];

  // Filter nav items based on permissions
  const navItems = allNavItems.filter(item => item.canAccess() && !(isClient && item.path === '/home'));

  const isActivePath = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const toggleMobileGroup = (path) => {
    setExpandedMobileGroups(groups => ({ ...groups, [path]: !groups[path] }));
  };

  const openMobileMenu = () => {
    setIsProfileMenuOpen(false);
    setIsMobileMenuOpen(true);
  };

  return (
    <div className="app-layout">
      {/* Top Navigation Bar */}
      <header className="top-navbar">
        <div className="navbar-left">
          <button
            type="button"
            className="mobile-menu-trigger"
            ref={mobileMenuTriggerRef}
            aria-label="Otwórz menu nawigacji"
            aria-controls="mobile-navigation"
            aria-expanded={isMobileMenuOpen}
            onClick={openMobileMenu}
          >
            <span aria-hidden="true" />
            <span aria-hidden="true" />
            <span aria-hidden="true" />
          </button>
          <h1 className="app-title">Portal pracowniczy "Matplast"</h1>
        </div>
        
        <nav className="main-nav">
          {navItems.map((item) => (
            item.hasDropdown ? (
              <div key={item.path} className="nav-dropdown-container">
                <Link
                  to={item.path}
                  className={`nav-link ${isActivePath(item.path) ? 'active' : ''}`}
                >
                  <span className="nav-icon"><NavigationIcon icon={item.icon} /></span>
                  <span className="nav-label">{item.label}</span>
                  <span className="dropdown-arrow">▼</span>
                </Link>
                <div className="nav-dropdown">
                  {item.dropdownItems.map((dropdownItem) => (
                    dropdownItem.children?.length ? (
                      <div className="nav-dropdown-group" key={dropdownItem.path}>
                        <Link to={dropdownItem.path} className="nav-dropdown-item">
                          <span className="nav-icon"><NavigationIcon icon={dropdownItem.icon} /></span>
                          <span>{dropdownItem.label}</span>
                          <span className="nav-dropdown-nested-arrow" aria-hidden="true">›</span>
                        </Link>
                        <div className="nav-dropdown-submenu">
                          {dropdownItem.children.map(child => (
                            <Link key={child.path} to={child.path} className="nav-dropdown-item">
                              <span className="nav-icon"><NavigationIcon icon={child.icon} /></span>
                              <span>{child.label}</span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Link key={dropdownItem.path} to={dropdownItem.path} className="nav-dropdown-item">
                        <span className="nav-icon"><NavigationIcon icon={dropdownItem.icon} /></span>
                        <span>{dropdownItem.label}</span>
                      </Link>
                    )
                  ))}
                </div>
              </div>
            ) : (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-link ${isActivePath(item.path) ? 'active' : ''}`}
              >
                <span className="nav-icon"><NavigationIcon icon={item.icon} /></span>
                <span className="nav-label">{item.label}</span>
              </Link>
            )
          ))}
        </nav>

        <div
          className={`header-profile ${isProfileMenuOpen ? 'open' : ''}`}
          ref={profileMenuRef}
          onMouseEnter={() => setIsProfileMenuOpen(true)}
          onMouseLeave={() => setIsProfileMenuOpen(false)}
        >
          <button
            type="button"
            className="header-profile-trigger"
            aria-label={`Menu użytkownika: ${user?.full_name || 'Użytkownik'}`}
            aria-haspopup="menu"
            aria-expanded={isProfileMenuOpen}
            onClick={() => setIsProfileMenuOpen(open => !open)}
          >
            {showAvatarImage ? (
              <img src={avatarUrl} alt="" onError={() => setAvatarLoadFailed(true)} />
            ) : (
              <span>{getUserInitials()}</span>
            )}
            {unreadCount > 0 && <span className="header-profile-badge" aria-label={`${unreadCount} nieprzeczytanych powiadomień`} />}
          </button>

          <div className="header-profile-menu" role="menu">
            <div className="header-profile-summary">
              <strong>{user?.full_name || 'Użytkownik'}</strong>
              <span>{user?.email}</span>
            </div>
            {profileMenuItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                className={`header-profile-item ${location.pathname === item.path ? 'active' : ''}`}
                role="menuitem"
              >
                <MenuIcon name={item.icon} />
                <span>{item.label}</span>
                {item.badge > 0 && <span className="notification-badge">{item.badge > 99 ? '99+' : item.badge}</span>}
              </Link>
            ))}
            {adminContentMenuItems.length > 0 && (
              <>
                <div className="header-profile-divider" />
                <p className="header-profile-section-label">Admin</p>
                {adminContentMenuItems.map(item => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`header-profile-item ${location.pathname === item.path ? 'active' : ''}`}
                    role="menuitem"
                  >
                    <MenuIcon name={item.icon} />
                    <span>{item.label}</span>
                  </Link>
                ))}
              </>
            )}
            <div className="header-profile-divider" />
            <button type="button" className="header-profile-item logout" role="menuitem" onClick={logout}>
              <MenuIcon name="logout" />
              <span>Wyloguj</span>
            </button>
          </div>
        </div>

      </header>

      <div
        className={`mobile-navigation-layer ${isMobileMenuOpen ? 'open' : ''}`}
        aria-hidden={!isMobileMenuOpen}
      >
        <button
          type="button"
          className="mobile-navigation-backdrop"
          aria-label="Zamknij menu nawigacji"
          tabIndex={isMobileMenuOpen ? 0 : -1}
          onClick={() => setIsMobileMenuOpen(false)}
        />
        <aside
          id="mobile-navigation"
          className="mobile-navigation-drawer"
          aria-label="Nawigacja mobilna"
          aria-modal="true"
          role="dialog"
        >
          <div className="mobile-navigation-header">
            <div>
              <strong>Menu</strong>
              <span>Portal pracowniczy Matplast</span>
            </div>
            <button
              type="button"
              className="mobile-navigation-close"
              ref={mobileMenuCloseRef}
              aria-label="Zamknij menu"
              onClick={() => {
                setIsMobileMenuOpen(false);
                mobileMenuTriggerRef.current?.focus();
              }}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>

          <nav className="mobile-navigation-list" aria-label="Główne moduły">
            {navItems.map(item => {
              const hasChildren = item.hasDropdown && item.dropdownItems?.length > 0;
              const isExpanded = expandedMobileGroups[item.path] ?? isActivePath(item.path);

              return (
                <div className="mobile-navigation-group" key={item.path}>
                  <div className="mobile-navigation-row">
                    <Link
                      to={item.path}
                      className={`mobile-navigation-link ${isActivePath(item.path) ? 'active' : ''}`}
                      tabIndex={isMobileMenuOpen ? 0 : -1}
                    >
                      <span className="mobile-navigation-icon" aria-hidden="true"><NavigationIcon icon={item.icon} /></span>
                      <span>{item.label}</span>
                    </Link>
                    {hasChildren && (
                      <button
                        type="button"
                        className="mobile-navigation-expand"
                        aria-label={`${isExpanded ? 'Zwiń' : 'Rozwiń'}: ${item.label}`}
                        aria-expanded={isExpanded}
                        tabIndex={isMobileMenuOpen ? 0 : -1}
                        onClick={() => toggleMobileGroup(item.path)}
                      >
                        <span aria-hidden="true">⌄</span>
                      </button>
                    )}
                  </div>
                  {hasChildren && (
                    <div className={`mobile-navigation-submenu ${isExpanded ? 'open' : ''}`}>
                      <div>
                        {item.dropdownItems.map(child => (
                          child.children?.length ? (
                            <div className="mobile-navigation-nested-group" key={child.path}>
                              <Link
                                to={child.path}
                                className={`mobile-navigation-sublink ${isActivePath(child.path) ? 'active' : ''}`}
                                tabIndex={isMobileMenuOpen && isExpanded ? 0 : -1}
                              >
                                <span aria-hidden="true"><NavigationIcon icon={child.icon} /></span>
                                <span>{child.label}</span>
                              </Link>
                              {child.children.map(nestedChild => (
                                <Link
                                  key={nestedChild.path}
                                  to={nestedChild.path}
                                  className={`mobile-navigation-sublink mobile-navigation-sublink--nested ${isActivePath(nestedChild.path) ? 'active' : ''}`}
                                  tabIndex={isMobileMenuOpen && isExpanded ? 0 : -1}
                                >
                                  <span aria-hidden="true"><NavigationIcon icon={nestedChild.icon} /></span>
                                  <span>{nestedChild.label}</span>
                                </Link>
                              ))}
                            </div>
                          ) : (
                            <Link
                              key={child.path}
                              to={child.path}
                              className={`mobile-navigation-sublink ${isActivePath(child.path) ? 'active' : ''}`}
                              tabIndex={isMobileMenuOpen && isExpanded ? 0 : -1}
                            >
                              <span aria-hidden="true"><NavigationIcon icon={child.icon} /></span>
                              <span>{child.label}</span>
                            </Link>
                          )
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </aside>
      </div>

      <div className="app-body">
        {/* Main Content Area */}
        <main className="main-content expanded">
          {children}
        </main>
      </div>

      {/* Feedback is intentionally hidden throughout the Montaż module. */}
      {!isClient && !location.pathname.startsWith('/montaz') && <FeedbackWidget />}
    </div>
  );
};

export default Layout;
